import React from 'react';
import AnalysisTextSection from './AnalysisTextSection';
import { Registry } from '@/graphql/__generated__/onboarding';
import { useNormalizedAntifraudData } from '@/hooks/useNormalizedAntifraudData';
import { useTranslations } from 'use-intl';
import { NormalizedAntifraudData } from '@/utils/antifraudDataNormalizer';

interface OfficialIdProps {
	registry: Registry;
	normalizedData?: NormalizedAntifraudData | null;
	setDetailedAnswerModalContent?: (value: any) => void;
	setOpenDetailedAnswerModal?: (value: boolean) => void;
}

export const OfficialId: React.FC<OfficialIdProps> = ({
	registry,
	normalizedData: propNormalizedData,
	setDetailedAnswerModalContent,
	setOpenDetailedAnswerModal
}) => {
	const t = useTranslations('RegistryDetails');
	const hookNormalizedData = useNormalizedAntifraudData(registry);
	const normalizedData = propNormalizedData ?? hookNormalizedData;

	if (
		!normalizedData?.ine &&
		!normalizedData?.selfieAndOfficialIdMatch &&
		!normalizedData?.officialIdFaceRecognition &&
		!normalizedData?.selfieFaceRecognition
	) {
		return null;
	}

	const getExistsInRenapo = (): boolean | null => {
		if (!normalizedData?.officialIdCurp) return null;

		if (normalizedData.officialIdCurp.error) {
			return false;
		}
		if (normalizedData.officialIdCurp.isValid === true) {
			return true;
		}
		if (normalizedData.officialIdCurp.isValid === false) {
			return false;
		}
		return null;
	};

	const existsInRenapo = getExistsInRenapo();
	const isUnico = normalizedData?.isUnico || false;

	return (
		<div className="space-y-3">
			{normalizedData?.ine?.curp && (
				<AnalysisTextSection label="CURP" value={normalizedData.ine.curp} copyable={true} />
			)}
			{existsInRenapo !== null && (
				<AnalysisTextSection label="ExistsInRenapo" value={existsInRenapo} />
			)}
			{normalizedData?.ine?.electoralKey && (
				<AnalysisTextSection
					label="ElectoralKey"
					value={normalizedData.ine.electoralKey}
					copyable={true}
				/>
			)}
			{normalizedData?.ine?.names && (
				<AnalysisTextSection label="Names" value={normalizedData.ine.names} copyable={true} />
			)}
			{normalizedData?.ine?.firstLastName && (
				<AnalysisTextSection
					label="FirstLastName"
					value={normalizedData.ine.firstLastName}
					copyable={true}
				/>
			)}
			{normalizedData?.ine?.secondLastName && (
				<AnalysisTextSection
					label="SecondLastName"
					value={normalizedData.ine.secondLastName}
					copyable={true}
				/>
			)}
			{normalizedData?.ine?.validThru && (
				<AnalysisTextSection
					label="ValidThru"
					value={normalizedData.ine.validThru}
					copyable={true}
				/>
			)}
			{isUnico && normalizedData?.ine?.registrationYear && (
				<AnalysisTextSection
					label="RegistrationYear"
					value={normalizedData.ine.registrationYear}
					copyable={true}
				/>
			)}
			{isUnico && normalizedData?.ine?.sex !== null && normalizedData?.ine?.sex !== undefined && (
				<AnalysisTextSection label="Sex" value={normalizedData.ine.sex} />
			)}
			{normalizedData?.ine?.cic && (
				<AnalysisTextSection
					label="CIC"
					value={normalizedData.ine.cic}
					tooltip={t('CICTooltip')}
					copyable={true}
				/>
			)}
			{normalizedData?.ine?.citizenId && (
				<AnalysisTextSection
					label="CitizenId"
					value={normalizedData.ine.citizenId}
					copyable={true}
				/>
			)}
			{normalizedData?.ine?.mrz && (
				<AnalysisTextSection
					label="MRZ"
					value={normalizedData.ine.mrz}
					setDetailedAnswerModalContent={setDetailedAnswerModalContent}
					setOpenDetailedAnswerModal={setOpenDetailedAnswerModal}
					tooltip={t('MRZTooltip')}
					copyable={true}
				/>
			)}
			{normalizedData?.selfieAndOfficialIdMatch !== null &&
				normalizedData?.selfieAndOfficialIdMatch !== undefined && (
					<AnalysisTextSection
						label="FaceMatchWithSelfie"
						value={normalizedData.selfieAndOfficialIdMatch}
					/>
				)}
			{normalizedData?.officialIdFaceRecognition?.warnings && (
				<AnalysisTextSection
					label="OfficialIdFaceWarnings"
					value={normalizedData.officialIdFaceRecognition.warnings}
					setDetailedAnswerModalContent={setDetailedAnswerModalContent}
					setOpenDetailedAnswerModal={setOpenDetailedAnswerModal}
				/>
			)}
			{normalizedData?.officialIdFaceRecognition?.confidenceScore && (
				<AnalysisTextSection
					label="OfficialIdConfidenceScore"
					value={normalizedData.officialIdFaceRecognition.confidenceScore}
				/>
			)}
			{normalizedData?.selfieFaceRecognition?.warnings && (
				<AnalysisTextSection
					label="SelfieFaceWarnings"
					value={normalizedData.selfieFaceRecognition.warnings}
					setDetailedAnswerModalContent={setDetailedAnswerModalContent}
					setOpenDetailedAnswerModal={setOpenDetailedAnswerModal}
				/>
			)}
			{normalizedData?.selfieFaceRecognition?.confidenceScore && (
				<AnalysisTextSection
					label="SelfieConfidenceScore"
					value={normalizedData.selfieFaceRecognition.confidenceScore}
				/>
			)}
			{isUnico && normalizedData?.officialIdFaceRecognition?.lastSeenByYourCompany && (
				<AnalysisTextSection
					label="FaceLastSeenByYourCompany"
					value={normalizedData.officialIdFaceRecognition.lastSeenByYourCompany}
				/>
			)}
			{isUnico &&
				normalizedData?.officialIdFaceRecognition?.matchFraudFlag !== null &&
				normalizedData?.officialIdFaceRecognition?.matchFraudFlag !== undefined && (
					<AnalysisTextSection
						label="FaceMatchFraudFlag"
						value={normalizedData.officialIdFaceRecognition.matchFraudFlag}
					/>
				)}
			{isUnico && normalizedData?.selfieFaceRecognition?.lastSeenByYourCompany && (
				<AnalysisTextSection
					label="SelfieLastSeenByYourCompany"
					value={normalizedData.selfieFaceRecognition.lastSeenByYourCompany}
				/>
			)}
			{isUnico &&
				normalizedData?.selfieFaceRecognition?.matchFraudFlag !== null &&
				normalizedData?.selfieFaceRecognition?.matchFraudFlag !== undefined && (
					<AnalysisTextSection
						label="SelfieMatchFraudFlag"
						value={normalizedData.selfieFaceRecognition.matchFraudFlag}
					/>
				)}
		</div>
	);
};

export default OfficialId;
